/**
 * Banner Tracking for GA4 via Google Tag Manager
 * Tracks: Impressions and Clicks only
 */

(function() {
  'use strict';

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  /**
   * Track banner impression
   * @param {string} bannerName - Name of the banner
   * @param {string} bannerBrand - Brand associated with the banner
   * @param {string} bannerType - Type: 'slideshow' or 'static'
   * @param {number|null} slideIndex - Slide index (for slideshow) or null (for static)
   * @param {string} sectionId - Section ID
   */
  function trackBannerImpression(bannerName, bannerBrand, bannerType, slideIndex, sectionId) {
    const impressionData = {
      event: 'banner_impression',
      banner_name: bannerName,
      banner_brand: bannerBrand,
      banner_type: bannerType,
      banner_slide_index: slideIndex,
      banner_section_id: sectionId,
      page_url: window.location.href,
      timestamp: new Date().toISOString()
    };

    // Push to dataLayer for GTM (GTM will handle sending to GA4)
    window.dataLayer.push(impressionData);

    console.log('Banner impression tracked:', impressionData);
  }

  /**
   * Track banner click
   * @param {string} bannerName - Name of the banner
   * @param {string} bannerBrand - Brand associated with the banner
   * @param {string} bannerLink - URL the banner links to
   * @param {string} bannerType - Type: 'slideshow' or 'static'
   * @param {number|null} slideIndex - Slide index (for slideshow) or null (for static)
   * @param {string} sectionId - Section ID
   */
  function trackBannerClick(bannerName, bannerBrand, bannerLink, bannerType, slideIndex, sectionId) {
    const clickData = {
      event: 'banner_click',
      banner_name: bannerName,
      banner_brand: bannerBrand,
      banner_link: bannerLink,
      banner_type: bannerType,
      banner_slide_index: slideIndex,
      banner_section_id: sectionId,
      page_url: window.location.href,
      timestamp: new Date().toISOString()
    };

    // Push to dataLayer for GTM (GTM will handle sending to GA4)
    window.dataLayer.push(clickData);

    console.log('Banner click tracked:', clickData);
  }

  /**
   * Initialize slideshow banner tracking
   */
  function initSlideshowTracking() {
    // Find all slideshow components
    const slideshows = document.querySelectorAll('slideshow-component');

    slideshows.forEach(slideshow => {
      const slideshowId = slideshow.id || slideshow.querySelector('[id^="Slider-"]')?.id || 'slideshow';
      const slides = slideshow.querySelectorAll('.slideshow__slide');

      // Track initial slide impression
      const firstSlide = slides[0];
      if (firstSlide) {
        const bannerLink = firstSlide.querySelector('.banner-link');
        if (bannerLink) {
          const bannerName = bannerLink.dataset.bannerName || 'Unknown';
          const bannerBrand = bannerLink.dataset.bannerBrand || 'Unknown';
          const slideIndex = Array.from(slides).indexOf(firstSlide) + 1;
          
          // Use IntersectionObserver to ensure slide is actually visible
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                trackBannerImpression(bannerName, bannerBrand, 'slideshow', slideIndex, slideshowId);
                observer.unobserve(entry.target);
              }
            });
          }, { threshold: 0.5 });

          observer.observe(firstSlide);
        }
      }

      // Listen for slide changes
      slideshow.addEventListener('slideChanged', (event) => {
        const currentElement = event.detail.currentElement;
        if (!currentElement) return;

        const bannerLink = currentElement.querySelector('.banner-link');
        if (bannerLink) {
          const bannerName = bannerLink.dataset.bannerName || 'Unknown';
          const bannerBrand = bannerLink.dataset.bannerBrand || 'Unknown';
          const slideIndex = event.detail.currentPage;
          
          // Track impression with slight delay to ensure slide is fully visible
          setTimeout(() => {
            trackBannerImpression(bannerName, bannerBrand, 'slideshow', slideIndex, slideshowId);
          }, 100);
        }
      });

      // Track banner clicks
      slides.forEach((slide, index) => {
        const bannerLink = slide.querySelector('.banner-link');
        if (bannerLink) {
          bannerLink.addEventListener('click', (event) => {
            const bannerName = bannerLink.dataset.bannerName || 'Unknown';
            const bannerBrand = bannerLink.dataset.bannerBrand || 'Unknown';
            const bannerLinkUrl = bannerLink.href || '';
            const slideIndex = index + 1;

            trackBannerClick(bannerName, bannerBrand, bannerLinkUrl, 'slideshow', slideIndex, slideshowId);
          });
        }
      });
    });
  }

  /**
   * Initialize static banner tracking
   */
  function initStaticBannerTracking() {
    // Find all static banners with data attributes
    const staticBanners = document.querySelectorAll('[data-banner-type="static"]');

    staticBanners.forEach(banner => {
      // Get banner attributes from the container element
      const bannerName = banner.dataset.bannerName || 'Unknown';
      const bannerBrand = banner.dataset.bannerBrand || 'Unknown';
      const sectionId = banner.dataset.bannerSectionId || banner.id || 'static-banner';

      // Track impression when banner comes into view
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackBannerImpression(bannerName, bannerBrand, 'static', null, sectionId);
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });

      observer.observe(banner);

      // Track clicks on ALL links within the banner (for collection cards with multiple links)
      // Use event delegation on the banner container to catch all link clicks
      banner.addEventListener('click', (event) => {
        // Find the clicked link (closest anchor tag)
        const clickedLink = event.target.closest('a');
        
        if (clickedLink && clickedLink.href && !clickedLink.hasAttribute('aria-disabled')) {
          const linkUrl = clickedLink.href || '';
          trackBannerClick(bannerName, bannerBrand, linkUrl, 'static', null, sectionId);
        }
      });
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        initSlideshowTracking();
        initStaticBannerTracking();
      });
    } else {
      initSlideshowTracking();
      initStaticBannerTracking();
    }
  }

  // Initialize
  init();
})();

